import TableContainer from 'Components/Common/TableContainerReactTable';
import React, { useState } from 'react';
import {DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import UserEditModal from '../Modal/UserEditModal';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'slices/store';
import { formatFullName, formatStringWithUnderscores, getTruncatedId } from 'lib/utils/formatters';
import { setSelectedUserId } from 'slices/user/userSlice';
// Import social media icons
import googleIcon from 'assets/images/icons/google.svg';
import facebookIcon from 'assets/images/icons/facebook.svg';
import appleIcon from 'assets/images/icons/apple.svg';
// Import 2FA icons
import smsIcon from 'assets/images/icons/sms-notification.svg';
import authenticatorIcon from 'assets/images/icons/authenticator.svg';

interface RejectedListingProps {
    data: any[];
    isLoading?: boolean;
    currentPage?: number;
    totalPages?: number;
    totalCount?: number;
    onPageChange?: (page: number) => void;
    onRefresh?: () => void;
}

const RejectedListing = ({
    data,
    currentPage = 1,
    totalPages = 1,
    totalCount = 0,
    onPageChange,
    onRefresh
}: RejectedListingProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedPerson, setSelectedPerson] = useState<string>('');
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const customPageSize = 10; // Items per page

    // Function to copy ID to clipboard
    const copyToClipboard = async (fullId: string) => {
        try {
            await navigator.clipboard.writeText(fullId);
            setCopiedId(fullId);
            // Reset the tick icon after 2 seconds
            setTimeout(() => {
                setCopiedId(null);
            }, 1000);
        } catch (err) {
            console.error('Failed to copy ID:', err);
        }
    };

    const columns = [
        {
            header: 'No',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const index = (currentPage - 1) * customPageSize + row.index + 1;
                return <span>{index}</span>;
            },
        },
        {
            header: 'ID',
            accessorKey: 'id',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => {
                const fullId = getValue();
                if (!fullId) return 'N/A';

                return (
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '14px', fontFamily: 'monospace' }}>
                            {getTruncatedId(fullId)}
                        </span>
                        <span
                            onClick={() => copyToClipboard(fullId)}
                            style={{
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: copiedId === fullId ? '#28a745' : '#6b7280',
                                transition: 'color 0.2s ease'
                            }}
                            title={copiedId === fullId ? 'Copied!' : 'Copy full ID'}
                        >
                            {copiedId === fullId ? (
                                <i className="ri-check-line"></i>
                            ) : (
                                <i className="ri-file-copy-line"></i>
                            )}
                        </span>
                    </div>
                );
            },
        },
        {
            header: 'Name',
            accessorKey: 'name',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const { first_name, last_name } = row.original;
                return formatFullName(first_name, last_name);
            },
        },
        {
            header: 'Email',
            accessorKey: 'email',
            enableColumnFilter: false,
        },
        {
            header: 'Phone Number',
            accessorKey: 'phone_no',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => {
                const phoneNo = getValue();
                return phoneNo || 'N/A';
            },
        },
        {
            header: 'Created At',
            accessorKey: 'createdAt',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => {
                const date = getValue();
                if (!date) return 'N/A';
                const formattedDate = new Date(date).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });
                // Format date without comma
                const [day, month, year, time, ampm] = formattedDate.split(' ');
                return `${day} ${month} ${year} ${time} ${ampm}`;
            },
        },
        {
            header: 'Updated At',
            accessorKey: 'updatedAt',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => {
                const date = getValue();
                if (!date) return 'N/A';
                const formattedDate = new Date(date).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });
                // Format date without comma
                const [day, month, year, time, ampm] = formattedDate.split(' ');
                return `${day} ${month} ${year} ${time} ${ampm}`;
            },
        },
        {
            header: 'Social Accounts',
            accessorKey: 'social_accounts',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const data = row.original;
                const isGoogleConnected = data?.is_google_connected || false;
                const isFacebookConnected = data?.is_facebook_connected || false;
                const isAppleConnected = data?.is_apple_connected || false;

                return (
                    <div className="d-flex gap-2 align-items-center">
                        <img
                            src={googleIcon}
                            alt="Google"
                            className={isGoogleConnected ? '' : 'text-muted'}
                            style={{
                                width: '16px',
                                height: '16px',
                                opacity: isGoogleConnected ? 1 : 0.5,
                                filter: isGoogleConnected ? 'none' : 'grayscale(100%)'
                            }}
                            title={`Google ${isGoogleConnected ? 'Connected' : 'Not Connected'}`}
                        />
                        <img
                            src={facebookIcon}
                            alt="Facebook"
                            className={isFacebookConnected ? '' : 'text-muted'}
                            style={{
                                width: '16px',
                                height: '16px',
                                opacity: isFacebookConnected ? 1 : 0.5,
                                filter: isFacebookConnected ? 'none' : 'grayscale(100%)'
                            }}
                            title={`Facebook ${isFacebookConnected ? 'Connected' : 'Not Connected'}`}
                        />
                        <img
                            src={appleIcon}
                            alt="Apple"
                            className={isAppleConnected ? '' : 'text-muted'}
                            style={{
                                width: '16px',
                                height: '16px',
                                opacity: isAppleConnected ? 1 : 0.5,
                                filter: isAppleConnected ? 'none' : 'grayscale(100%)'
                            }}
                            title={`Apple ${isAppleConnected ? 'Connected' : 'Not Connected'}`}
                        />
                    </div>
                );
            },
        },
        {
            header: '2FA Setup',
            accessorKey: 'twofa_setup',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const data = row.original;
                const textOtp2fa = data?.text_otp_2fa || false;
                const authApp2fa = data?.auth_app_2fa || false;

                return (
                    <div className="d-flex gap-2 align-items-center">
                        <img
                            src={smsIcon}
                            alt="SMS"
                            className={textOtp2fa ? '' : 'text-muted'}
                            style={{
                                width: '20px',
                                height: '20px',
                                opacity: textOtp2fa ? 1 : 0.5,
                                filter: textOtp2fa ? 'none' : 'grayscale(100%)'
                            }}
                            title={`SMS 2FA ${textOtp2fa ? 'Enabled' : 'Disabled'}`}
                        />
                        <img
                            src={authenticatorIcon}
                            alt="Authenticator"
                            className={authApp2fa ? '' : 'text-muted'}
                            style={{
                                width: '20px',
                                height: '20px',
                                opacity: authApp2fa ? 1 : 0.5,
                                filter: authApp2fa ? 'none' : 'grayscale(100%)'
                            }}
                            title={`Auth App 2FA ${authApp2fa ? 'Enabled' : 'Disabled'}`}
                        />
                    </div>
                );
            },
        },
        {
            header: 'Status',
            accessorKey: 'status',
            enableColumnFilter: false,
            cell: (cell: any) => {
                const status = cell.getValue();
                const formattedStatus = formatStringWithUnderscores(status);
                switch (status) {
                    case "approved":
                    case "accepted":
                        return (<div className="badge rounded-pill bg-success">{formattedStatus}</div>);
                    case "pending":
                        return (<div className="badge rounded-pill bg-warning">{formattedStatus}</div>);
                    case "rejected":
                        return (<div className="badge rounded-pill bg-danger">{formattedStatus}</div>);
                    default:
                        return (<div className="badge rounded-pill bg-secondary">{formattedStatus || 'Unknown'}</div>);
                }
            },
        },
        {
            header: 'Action',
            accessorKey: 'action',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => (
                <UncontrolledDropdown direction="start">
                    <DropdownToggle tag="span" style={{ cursor: 'pointer' }}>
                        <i className="ri-more-2-fill" style={{ fontSize: 20, color: '#6b7280' }}></i>
                    </DropdownToggle>
                    <DropdownMenu>
                        <DropdownItem
                            onClick={() => {
                                const selectUserId = row?.original?.id;
                                dispatch(setSelectedUserId(selectUserId));
                                navigate(`/user-details`);
                            }}
                            className="d-flex align-items-center"
                        >
                            <i className="ri-eye-line me-2"></i> View Details
                        </DropdownItem>
                        <DropdownItem
                            onClick={() => {
                                setSelectedPerson(row?.original?.id);
                                setModalOpen(true);
                            }}
                            className="d-flex align-items-center"
                        >
                            <i className="ri-pencil-line me-2"></i> Edit User
                        </DropdownItem>
                    </DropdownMenu>
                </UncontrolledDropdown>
            ),
        },
    ];

    return (
        <React.Fragment>
            <TableContainer
                columns={columns}
                data={data}
                customPageSize={customPageSize}
                tableClass="table-centered align-middle table-nowrap mb-0"
                theadClass="text-muted table-light"
                isServerSide={true}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={onPageChange}
            />
            <UserEditModal
                isOpen={modalOpen}
                toggle={() => {
                    setModalOpen(false);
                    setSelectedPerson('');
                }}
                user={selectedPerson}
                refetch={onRefresh}
            />
        </React.Fragment>
    );
};

export default RejectedListing;
